import React, { useCallback, useMemo, useRef, useState } from "react";
import { graphql } from "react-relay";
import { useHistory, useLocation } from "react-router";

import { faSearch } from "@awesome.me/kit-4c83d50d0d/icons/classic/regular";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { debounce } from "lodash";

import { useLazyLoadQuery } from "~/lib/relayUtil";
import { useClearSearchParam } from "~/search/filters/Filter";
import Avatar from "~/ui/avatar";
import { TextField } from "~/ui/legacyFellowIndex";
import Menu from "~/ui/menu";

import { ATTENDEE_TYPE_PARAM_NAME } from "../tabsAndFilters/TabsAndFilters";

import type { AttendeeFilterAttendeesQuery } from "~/__generated__/AttendeeFilterAttendeesQuery.graphql";

import styles from "./AttendeeFilter.module.css";
import filtersStyles from "./Filters.module.css";

const attendeesQuery = graphql`
	query AttendeeFilterAttendeesQuery($query: String) {
		recordingsAttendees(search: $query) {
			email
			fullName
			image
		}
	}
`;

type AttendeeType = AttendeeFilterAttendeesQuery["response"]["recordingsAttendees"][0];

export const PARAM_NAME = "with";
const TRIGGER_TEXT = "With";
const EXTERNAL_USERS = "External users";

function isDomain(text: string) {
	const domainRegex = /^@[a-zA-Z\d-]+\.[a-zA-Z]{2,}$/;
	return domainRegex.test(text);
}

export function AttendeeFilterTriggerContent({
	selectedUsers,
	isExternalUsers,
}: {
	selectedUsers?: Array<AttendeeType>;
	isExternalUsers?: boolean;
}) {
	if (isExternalUsers) {
		return (
			<span className={styles.triggerContent}>
				<span>
					{TRIGGER_TEXT}:&nbsp;{EXTERNAL_USERS}
				</span>
			</span>
		);
	}

	if (!selectedUsers || selectedUsers?.length === 0) return <>{TRIGGER_TEXT}</>;

	const firstUser = selectedUsers[0];
	const isEmailDomain = isDomain(firstUser.email);

	return (
		<span className={styles.triggerContent}>
			<span>{TRIGGER_TEXT}:&nbsp;</span>
			{isEmailDomain ? (
				<span>{firstUser.email}</span>
			) : (
				<>
					<Avatar
						user={{
							...firstUser,
							fullName: firstUser.fullName || firstUser.email,
							image: firstUser.image || undefined,
						}}
						size="small"
					/>
					{selectedUsers[0].fullName && <span>&nbsp;{selectedUsers[0].fullName}</span>}
				</>
			)}
			{selectedUsers.length > 1 && (
				<span>
					+ {selectedUsers.length - 1} other{selectedUsers.length - 1 > 1 ? "s" : ""}
				</span>
			)}
		</span>
	);
}

interface OptionProps {
	searchParam: string;
	attendee: AttendeeType;
	selected: boolean;
	toggleUser: (user: AttendeeType) => void;
}

export function Option({ searchParam, attendee, selected, toggleUser }: OptionProps) {
	const history = useHistory();
	const location = useLocation();

	const onCheckedChange = useCallback(
		(selected: boolean) => {
			const search = new URLSearchParams(location.search);
			const emails = new Set(search.get(searchParam)?.split(",") || []);
			if (selected) {
				emails.add(attendee.email);
			} else {
				emails.delete(attendee.email);
			}
			if (emails.size > 0) {
				search.set(searchParam, Array.from(emails).join(","));
			} else {
				search.delete(searchParam);
			}
			history.replace({
				pathname: location.pathname,
				search: search.toString(),
			});

			toggleUser(attendee);
		},
		[location.search, location.pathname, searchParam, history, toggleUser, attendee],
	);

	return (
		<Menu.CheckboxItem checked={selected} onCheckedChange={onCheckedChange} className={styles.userFilterItem}>
			<Avatar
				user={{
					...attendee,
					fullName: attendee.fullName || attendee.email,
					image: attendee.image || undefined,
				}}
				size="small"
			/>
			{attendee.fullName || attendee.email}
		</Menu.CheckboxItem>
	);
}

export default function AttendeesFilter({ attendeeType }: { attendeeType?: string | null }) {
	const location = useLocation();
	const [query, setQuery] = useState<string>();
	const [inputValue, setInputValue] = useState("");
	const [selectedUsers, setSelectedUsers] = useState<Array<AttendeeType>>(() => {
		const search = new URLSearchParams(location.search);
		const selectedEmails =
			search
				.get(PARAM_NAME)
				?.split(",")
				.filter(email => Boolean(email)) ?? [];
		return selectedEmails.map(email => ({ email, fullName: null, image: null }));
	});

	const toggleUser = useCallback((user: AttendeeType) => {
		setSelectedUsers(users => {
			const userIndex = users.findIndex(u => u.email === user.email);
			if (userIndex === -1) {
				return [...users, user];
			}
			return users.filter(u => u.email !== user.email);
		});
	}, []);

	const debouncedSearchQuery = useMemo(() => {
		return debounce(value => {
			setQuery(value);
		}, 350);
	}, []);

	const setValue = useCallback(
		newValue => {
			setInputValue(newValue);
			debouncedSearchQuery(newValue);
		},
		[debouncedSearchQuery],
	);

	const onTextFieldKeyDown = useCallback((event: React.KeyboardEvent<HTMLInputElement>) => {
		event.stopPropagation();
	}, []);

	const inputRef = useRef<HTMLInputElement>(null);

	const onInputFocus = useCallback(() => {
		inputRef.current?.focus();
	}, [inputRef]);

	const onClear = useClearSearchParam(PARAM_NAME, [PARAM_NAME, ATTENDEE_TYPE_PARAM_NAME]);
	const isExternalUsers = attendeeType === "external";

	return (
		<Menu.Sub>
			<Menu.SubTrigger>{TRIGGER_TEXT}…</Menu.SubTrigger>
			<Menu.SubContent onFocus={onInputFocus}>
				<div className={styles.search}>
					<FontAwesomeIcon icon={faSearch} color="var(--secondaryTextColor)" className="search-icon" />
					<TextField
						value={inputValue}
						setValue={setValue}
						onKeyDown={onTextFieldKeyDown}
						ref={inputRef}
						placeholder="Search users"
						autoFocus
						className={styles.searchInput}
					/>
				</div>
				<Menu.Separator />
				<React.Suspense fallback={<span className={styles.loadingState}>Loading…</span>}>
					<UsersRenderer
						isExternalUsers={isExternalUsers}
						query={query}
						searchParam={PARAM_NAME}
						selectedUsers={selectedUsers}
						toggleUser={toggleUser}
					/>
				</React.Suspense>
				{(selectedUsers.length !== 0 || isExternalUsers) && (
					<>
						<Menu.Separator />
						<Menu.Item onSelect={onClear} className={filtersStyles.clearFilterOption}>
							Clear filter
						</Menu.Item>
					</>
				)}
			</Menu.SubContent>
		</Menu.Sub>
	);
}

interface UsersRendererProps {
	query?: string;
	searchParam: string;
	selectedUsers: Array<AttendeeType>;
	toggleUser: (user: AttendeeType) => void;
	isExternalUsers: boolean;
}

function UsersRenderer({ query, searchParam, selectedUsers, toggleUser, isExternalUsers }: UsersRendererProps) {
	const data = useLazyLoadQuery<AttendeeFilterAttendeesQuery>(
		attendeesQuery,
		{ query },
		{ fetchPolicy: "store-or-network" },
	);

	const history = useHistory();
	const location = useLocation();

	const onExternalUserCheckedChange = useCallback(
		(selected: boolean) => {
			const search = new URLSearchParams(location.search);
			if (selected) {
				search.set("attendeeType", "external");
			} else {
				search.delete("attendeeType");
			}

			history.replace({
				pathname: location.pathname,
				search: search.toString(),
			});
		},
		[location.search, location.pathname, history],
	);

	const onCheckedChange = useCallback(
		(selected: boolean) => {
			const search = new URLSearchParams(location.search);
			const emails = new Set(search.get(searchParam)?.split(",") || []);
			if (selected && query) {
				emails.add(query);
			} else {
				emails.delete(query || "");
			}
			if (emails.size > 0) {
				search.set(searchParam, Array.from(emails).join(","));
			} else {
				search.delete(searchParam);
			}
			history.replace({
				pathname: location.pathname,
				search: search.toString(),
			});
			if (query) {
				toggleUser({ email: query, fullName: null, image: null });
			}
		},
		[location.search, location.pathname, searchParam, history, query, toggleUser],
	);

	const usersToRender = useMemo(() => {
		return [
			...selectedUsers,
			...data.recordingsAttendees.filter(
				user => !selectedUsers.find(selectedUser => user.email === selectedUser.email),
			),
		];
	}, [selectedUsers, data]);

	const EXTERNAL_USERS = "External users";
	const hideExternalUsersOption =
		!isExternalUsers && !EXTERNAL_USERS.toLowerCase().includes(query?.toLowerCase() || "");

	const hasQueryInUsersToRender = usersToRender.some(user => user.email.toLowerCase() === query?.toLowerCase() || "");

	return (
		<>
			{!hasQueryInUsersToRender && hideExternalUsersOption && (
				<Menu.CheckboxItem
					checked={!!selectedUsers.find(selectedUser => query === selectedUser.email)}
					onCheckedChange={onCheckedChange}
					className={styles.userFilterItem}
				>
					{query}
				</Menu.CheckboxItem>
			)}
			{hideExternalUsersOption || (
				<Menu.CheckboxItem
					checked={isExternalUsers}
					onCheckedChange={onExternalUserCheckedChange}
					className={styles.userFilterItem}
				>
					{EXTERNAL_USERS}
				</Menu.CheckboxItem>
			)}
			{usersToRender.map(user => (
				<Option
					key={user.email}
					searchParam={searchParam}
					attendee={user}
					selected={!!selectedUsers.find(selectedUser => user.email === selectedUser.email)}
					toggleUser={toggleUser}
				/>
			))}
		</>
	);
}
